import { CircleUserRound, Home, ShoppingBag, Trophy } from 'lucide-react';
import type { ReactNode } from 'react';
import { LockOnIcon } from './icons';

export type AppTab = 'quests' | 'ranks' | 'shop' | 'profile';

export interface AppShellLabels {
  quests: string;
  ranks: string;
  scan: string;
  shop: string;
  profile: string;
}

export interface AppShellProps {
  /** The active routed tab. */
  active: AppTab;
  /** Localized nav labels (inject from @ec2/i18n). */
  labels: AppShellLabels;
  onNavigate: (tab: AppTab) => void;
  /** The hero action — raised center Scan button. */
  onScan: () => void;
  /** 8px --danger badge dots on tab icons (e.g. new quest). */
  badges?: Partial<Record<AppTab, boolean>>;
  /**
   * Full-takeover mode (scanner / camera capture): hides the nav.
   * Brief addendum: everything else keeps the fixed nav, always.
   */
  hideNav?: boolean;
  children: ReactNode;
  className?: string;
}

const NAV_HEIGHT = 64;

const TABS: { id: AppTab; icon: typeof Home }[] = [
  { id: 'quests', icon: Home },
  { id: 'ranks', icon: Trophy },
];
const TABS_AFTER: { id: AppTab; icon: typeof Home }[] = [
  { id: 'shop', icon: ShoppingBag },
  { id: 'profile', icon: CircleUserRound },
];

function NavTab({
  id,
  icon: Icon,
  label,
  active,
  badge,
  onNavigate,
}: {
  id: AppTab;
  icon: typeof Home;
  label: string;
  active: boolean;
  badge?: boolean;
  onNavigate: (tab: AppTab) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(id)}
      aria-current={active ? 'page' : undefined}
      data-testid={`ec-nav-${id}`}
      className={
        `relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ` +
        `focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ` +
        `${active ? 'text-primary' : 'text-fg-secondary'}`
      }
    >
      {active && <span aria-hidden className="absolute top-1.5 h-[3px] w-3.5 rounded-sm bg-primary" />}
      <span className="relative">
        <Icon size={24} strokeWidth={2} aria-hidden />
        {badge && (
          <span aria-hidden data-testid="ec-nav-badge" className="absolute -right-1 -top-1 size-2 rounded-full bg-danger" />
        )}
      </span>
      <span className="max-w-full truncate">{label}</span>
    </button>
  );
}

/**
 * Participant app shell (brief §6 + addendum): position-fixed 64px bottom nav with
 * safe-area padding, five slots, the center one a raised 56px circular Scan button
 * (--primary fill, lock-on glyph) overlapping the bar by 14px. Content scrolls
 * underneath with padding-bottom = nav height + overlap + safe area.
 */
export function AppShell({
  active,
  labels,
  onNavigate,
  onScan,
  badges = {},
  hideNav = false,
  children,
  className = '',
}: AppShellProps) {
  return (
    <div data-testid="ec-app-shell" className={`min-h-dvh bg-bg text-fg ${className}`}>
      <main
        className="mx-auto w-full max-w-md"
        style={
          hideNav
            ? undefined
            : { paddingBottom: `calc(${NAV_HEIGHT + 14}px + env(safe-area-inset-bottom))` }
        }
      >
        {children}
      </main>
      {!hideNav && (
        <nav
          data-testid="ec-bottom-nav"
          className="ec-safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface"
        >
          <div className="relative mx-auto flex h-16 max-w-md items-stretch">
            {TABS.map((tab) => (
              <NavTab
                key={tab.id}
                {...tab}
                label={labels[tab.id]}
                active={active === tab.id}
                badge={badges[tab.id]}
                onNavigate={onNavigate}
              />
            ))}
            <div className="relative w-[78px] flex-none">
              <button
                type="button"
                onClick={onScan}
                aria-label={labels.scan}
                data-testid="ec-nav-scan"
                className={
                  `absolute -top-3.5 left-1/2 flex size-14 -translate-x-1/2 items-center justify-center ` +
                  `rounded-full bg-primary text-primary-fg shadow-[0_6px_18px] shadow-primary/45 ` +
                  `focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`
                }
              >
                <LockOnIcon size={26} aria-hidden />
              </button>
            </div>
            {TABS_AFTER.map((tab) => (
              <NavTab
                key={tab.id}
                {...tab}
                label={labels[tab.id]}
                active={active === tab.id}
                badge={badges[tab.id]}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
